import { useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router';
import { UserContext } from '~/app';
import { LoadingSpinner } from '~/components';
import { authDebug, summarizeUrl } from './authDebug';
import type { PostAuthDestination, PostAuthRole } from './postAuthRedirect';
import { resolvePostAuthDestination } from './postAuthRedirect';
import {
  CLERK_SATELLITE_URL_PARAM,
  RETURN_TO_PARAM,
  isAppPath,
  sanitizeReturnTo,
  sanitizeSatelliteReturnUrl,
} from './returnTo';
import { paths } from './routes';

const getSatelliteReturnPath = (satelliteReturnUrl: string | null): string | null => {
  if (!satelliteReturnUrl) return null;

  const url = new URL(satelliteReturnUrl);
  if (url.pathname === paths.publicAuth.authComplete) {
    return sanitizeReturnTo(url.searchParams.get(RETURN_TO_PARAM), url.origin);
  }
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Default Role Redirect Component
 *
 * Redirects to the appropriate dashboard based on user's current state:
 * - authenticatedProvider → vault dashboard
 * - authenticatedEmployer → HLX dashboard
 * - authenticatedImpersonating → vault dashboard
 * - registration states → role selection
 */
export const DefaultRoleRedirect = () => {
  const location = useLocation();
  const isProvider = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const isEmployer = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_EMPLOYER' })
  );
  const isImpersonating = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const impersonatingProviderId = UserContext.useSelector(
    (state) => state.context.impersonatingProviderId
  );
  const impersonatingProviderName = UserContext.useSelector(
    (state) => state.context.impersonatingProviderName
  );
  const isProviderRegistration = UserContext.useSelector((state) =>
    state.matches('$_PROVIDER_REGISTRATION')
  );
  const isEmployerRegistration = UserContext.useSelector((state) =>
    state.matches('$_EMPLOYER_REGISTRATION')
  );
  const needsRoleSelection = UserContext.useSelector((state) => state.matches('$_CHOOSE_ROLE'));
  // Clerk has flipped isSignedIn=true but the user-info fetch hasn't resolved yet.
  // Without this guard the component falls through to its catch-all and renders nothing.
  const isAuthLoading = UserContext.useSelector(
    (state) => state.matches('$_AUTHENTICATING') || state.matches('$_FETCHING_USER')
  );
  const satelliteReturnUrl = sanitizeSatelliteReturnUrl(
    new URLSearchParams(location.search).get(CLERK_SATELLITE_URL_PARAM)
  );
  const requestedPath = isAppPath(location.pathname)
    ? `${location.pathname}${location.search}${location.hash}`
    : getSatelliteReturnPath(satelliteReturnUrl);
  const role: PostAuthRole = isAuthLoading
    ? 'loading'
    : isProvider
      ? 'provider'
      : isImpersonating
        ? 'impersonating'
        : isEmployer
          ? 'employer'
          : isProviderRegistration
            ? 'registration-provider'
            : isEmployerRegistration
              ? 'registration-employer'
              : needsRoleSelection
                ? 'choose-role'
                : 'unknown';
  const destination: PostAuthDestination = useMemo(
    () =>
      resolvePostAuthDestination({
        role,
        hostname: window.location.hostname,
        returnTo: requestedPath,
        impersonation:
          impersonatingProviderId != null
            ? {
                providerId: impersonatingProviderId,
                providerName: impersonatingProviderName,
              }
            : undefined,
      }),
    [impersonatingProviderId, impersonatingProviderName, requestedPath, role]
  );

  useEffect(() => {
    if (destination.type !== 'external') return;

    authDebug('role-redirect.external', {
      role,
      requestedPath,
      targetUrl: summarizeUrl(destination.url),
    });
    window.location.assign(destination.url);
  }, [destination, requestedPath, role]);

  // Show a spinner during the user-state machine's post-login fetch window.
  // Returning null here is what produced the dev "white screen on Clerk
  // return" bug (MCV-1442): the window between Clerk flipping isSignedIn
  // and the role landing in `$_AUTHENTICATED_*` is a real network
  // round-trip, not an instant transition.
  //
  // Do NOT gate on `isClerkHandshake` here. Clerk strips the handshake
  // querystring via `history.replaceState`, which React Router's
  // `useLocation` does not observe — `location.search` stays stale until
  // the next `navigate()` call. Gating the render on a stale querystring
  // traps the user on a spinner forever (until they refresh). The
  // useEffect above already guards the only thing that matters about the
  // handshake window — cross-host `window.location.assign(...)` calls.
  // Returning a local `<Navigate />` from this render is safe even with a
  // stale handshake param: React Router navigation is same-host only.
  if (destination.type === 'loading') {
    return <LoadingSpinner />;
  }

  if (destination.type === 'external') {
    return null;
  }

  if (destination.type === 'local') {
    return <Navigate to={destination.path} replace />;
  }

  // Catch-all fallback: any unforeseen transient state should surface as a
  // visible loading indicator, not a silent blank page. If this ever sticks
  // it's a real bug — a stuck spinner is reportable, a white screen often isn't.
  return <LoadingSpinner />;
};
